/**
 * Material-UI theme configuration
 * Defines custom theme with typography, colors, and component overrides
 * Using WHO brand colors - light mode only
 */
import { createTheme, Theme, ThemeOptions, PaletteMode } from '@mui/material/styles';

/**
 * Creates theme - light mode only (dark mode removed)
 * @param _mode - Ignored, always returns light theme
 * @returns Configured theme
 */
export const createAppTheme = (_mode?: PaletteMode): Theme => {
    const themeOptions: ThemeOptions = {
        palette: {
            mode: 'light',
            primary: {
                main: '#0093d5', // WHO Blue
                light: '#33a9de',
                dark: '#006695',
                contrastText: '#ffffff',
            },
            secondary: {
                main: '#6B7280',
                light: '#9CA3AF',
                dark: '#4B5563',
                contrastText: '#ffffff',
            },
            error: {
                main: '#EF4444',
            },
            warning: {
                main: '#F59E0B',
            },
            info: {
                main: '#3B82F6',
            },
            success: {
                main: '#10B981',
            },
            background: {
                default: '#F5F5F5',
                paper: '#ffffff',
            },
            text: {
                primary: '#111827',
                secondary: '#6B7280',
            },
        },
        typography: {
            fontFamily: [
                '"Inter"',
                '-apple-system',
                'BlinkMacSystemFont',
                '"Segoe UI"',
                'Roboto',
                '"Helvetica Neue"',
                'Arial',
                'sans-serif',
            ].join(','),
            h1: {
                fontSize: '2.5rem',
                fontWeight: 600,
                letterSpacing: '-0.02em',
            },
            h2: {
                fontSize: '2rem',
                fontWeight: 600,
                letterSpacing: '-0.01em',
            },
            h3: {
                fontSize: '1.75rem',
                fontWeight: 600,
                letterSpacing: '-0.01em',
            },
            h4: {
                fontSize: '1.5rem',
                fontWeight: 600,
                letterSpacing: '-0.01em',
            },
            h5: {
                fontSize: '1.25rem',
                fontWeight: 600,
                letterSpacing: '-0.005em',
            },
            h6: {
                fontSize: '1rem',
                fontWeight: 600,
                letterSpacing: '0em',
            },
        },
        spacing: 8,
        shape: {
            borderRadius: 8,
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 600,
                    },
                },
            },
            MuiTextField: {
                defaultProps: {
                    variant: 'outlined',
                    size: 'medium',
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                    },
                },
            },
        },
    };

    return createTheme(themeOptions);
};

// Default theme export (light mode only)
export const theme = createAppTheme();
